/**
 * InvPreserveExplainer — progressive-disclosure explainer for INV.PRESERVE (I2).
 *
 * INV.PRESERVE is the F-coalgebra side of INV.CONSISTENCY. It neutralizes
 * the vulnerability class "transition violates an established invariant"
 * by typing the transition as a coalgebra map landing in F(Valid) rather
 * than F(S).
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { INV_PRESERVE_META } from "@csk/core";
import { THEOREM_COLOR, withAlpha } from "../../design-system/tokens.js";
import { MathInline, tex } from "../../components/math/index.js";
import {
  ExplainerCard,
  FCoalgebraDiagram,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";
import { TheoremMetaStrip } from "./TheoremMetaStrip.js";
import { TIER_ONE_IMMUNITY_LINK } from "./tierOneCrossLinks.js";

const ACCENT = THEOREM_COLOR["INV.PRESERVE"];

const IDENTITY: ExplainerIdentity = {
  id: "explainer-inv-preserve",
  kind: "SSM · Neutralization Theorem (S1)",
  title: "INV.PRESERVE",
  subtitle: "I2 · F-coalgebra · INERT · G3 Time",
  accent: ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "F-Coalgebra (structure)", href: "#explainer-fcoalgebra", accent: ACCENT },
  {
    label: "INV.ESTABLISH (dual)",
    href: "#explainer-inv-establish",
    accent: THEOREM_COLOR["INV.ESTABLISH"],
  },
  { label: "INV.CONSISTENCY (DPG)", href: "#explainer-inv-consistency" },
  { label: "Bisimulation", href: "#explainer-bisimulation" },
  TIER_ONE_IMMUNITY_LINK,
];

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      <strong>INV.PRESERVE</strong> says: every transition the system can observe of a valid state
      lands in another valid state. Transitions that would violate the invariant cannot be typed at
      all.
    </p>
  ),
  intuition: (
    <>
      <p>
        Once you have established an invariant, the next worry is "what if a step breaks it?" The
        standard answer is to write monitors that check the invariant after each step and roll back.
        CSK rejects that answer: monitor-based "preservation" is a runtime story that says "we will
        catch the violation," not "the violation cannot exist."
      </p>
      <p>
        Instead, CSK types the transition as an F-coalgebra into{" "}
        <MathInline>{tex`F(\mathrm{Valid})`}</MathInline>. There is no map of type{" "}
        <MathInline>{tex`\mathrm{Valid} \to F(\mathrm{Invalid})`}</MathInline> in the kernel
        category — the forbidden transition is unconstructable in the source.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        Let <MathInline>{tex`P : S \to \mathrm{Type}`}</MathInline> be the invariant predicate and
        write <MathInline>{tex`\mathrm{Valid} := \Sigma\, x.\, P\, x`}</MathInline>. The kernel
        exposes only transitions of type{" "}
        <MathInline>{tex`\gamma : \mathrm{Valid} \to F(\mathrm{Valid})`}</MathInline> — never{" "}
        <MathInline>{tex`S \to F(S)`}</MathInline>.
      </p>
      <p style={{ marginTop: "0.5rem" }}>
        Two valid states <MathInline>{tex`v_{1}, v_{2}`}</MathInline> map to the same point in{" "}
        <MathInline>{tex`\nu F`}</MathInline> iff they are bisimilar — observationally
        indistinguishable. This is how INV.PRESERVE certifies behavioral equivalence under
        refactoring.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        <strong>Vulnerability class neutralized:</strong> "transition violates an established
        invariant" — the unsound preserve event. Realized by structural inexpressibility: there is
        no Agda term of type <MathInline>{tex`\mathrm{Valid} \to F(\mathrm{Invalid})`}</MathInline>.
      </p>
      <p>
        <strong>Theorem-collision check.</strong> If you reach an "invalid state" by escaping into
        raw <MathInline>{tex`S`}</MathInline>, you have left the kernel category. INV.PRESERVE is
        then vacuous on the escape — the check has been bypassed by a typing-rule break, not by a
        logic bug.
      </p>
      <p>
        <strong>Seam type</strong> (per <code>theory/structural/SEAM-THEORY.md</code>): horizontal.{" "}
        <strong>Bridge</strong>: recovery witness (governance artifact).
      </p>
    </>
  ),
  references: [
    {
      label: "INV.PRESERVE theorem card",
      path: "docs/reference/theorem-cards/INV-PRESERVE.md",
    },
    {
      label: "Kernel module",
      path: `agda/${INV_PRESERVE_META.agdaSource}`,
      note: `INV_PRESERVE_META.agdaSource is "${INV_PRESERVE_META.agdaSource}"; the repo path is prefixed with "agda/".`,
    },
    {
      label: "Final coalgebra module",
      path: "agda/Kernel/INV/PRESERVE/FinalCoalgebra.agda",
      note: "Terminal F-coalgebra used by the preservation proof.",
    },
    {
      label: "Manuscript chapter (PR #1544)",
      path: "https://github.com/InvariantHoldings/categorical-safety-kernel/pull/1544",
      note: "Chapter 03 (F-Coalgebras / Invariant Preservation) lands with PR #1544's Structural Invariant Safety Guarantees Contrib manuscript.",
    },
  ],
};

const DIAGRAM = (
  <>
    <TheoremMetaStrip
      theoremName="INV.PRESERVE"
      code="I2"
      structure="F-coalgebra"
      mode="inert"
      generator="G3"
      seamType={INV_PRESERVE_META.seamType}
      bridgeArtifact="recovery witness"
      theoremColor={ACCENT}
      washColor={withAlpha(ACCENT, "1A")}
    />
    <div style={{ marginTop: "0.5rem" }}>
      <FCoalgebraDiagram accent={ACCENT} carrier="Valid" />
    </div>
  </>
);

export function InvPreserveExplainer(): React.JSX.Element {
  return (
    <ExplainerCard identity={IDENTITY} tiers={TIERS} diagram={DIAGRAM} crossLinks={CROSS_LINKS} />
  );
}
